import { create } from "zustand";
import { persist } from "zustand/middleware";

interface FileUrlEntry {
  url?: string; // permanent CDN URL (public, no expiry)
  base64?: string;
  mediaType: string;
}

interface FileUrlStore {
  urls: Map<string, FileUrlEntry>;
  set: (filePath: string, entry: FileUrlEntry) => void;
  get: (filePath: string) => FileUrlEntry | undefined;
}

const MAX_FILE_URL_ENTRIES = 500;

export const useFileUrlStore = create<FileUrlStore>()(
  persist(
    (set, get) => ({
      urls: new Map(),
      set: (filePath, entry) =>
        set((state) => {
          const next = new Map(state.urls);
          const existing = next.get(filePath);
          next.set(filePath, { ...existing, ...entry });
          // FIFO eviction — Map preserves insertion order
          if (next.size > MAX_FILE_URL_ENTRIES) {
            const keys = next.keys();
            for (let i = 0; i < next.size - MAX_FILE_URL_ENTRIES; i++) {
              const { value, done } = keys.next();
              if (done) break;
              if (value !== filePath) next.delete(value);
            }
          }
          return { urls: next };
        }),
      get: (filePath) => get().urls.get(filePath),
    }),
    {
      name: "bash-file-urls",
      storage: {
        getItem: (name) => {
          try {
            const str = localStorage.getItem(name);
            if (!str) return null;
            const parsed = JSON.parse(str);
            if (parsed?.state?.urls) {
              // Migrate old cdnUrl/signedUrl entries to new url field
              const entries = parsed.state.urls;
              const migrated = entries.map(([k, v]: [string, any]) => {
                if (v.cdnUrl || v.signedUrl) {
                  return [
                    k,
                    {
                      url: v.cdnUrl || v.signedUrl,
                      base64: v.base64,
                      mediaType: v.mediaType || "",
                    },
                  ];
                }
                return [k, v];
              });
              parsed.state.urls = new Map(migrated);
            }
            return parsed;
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          // Only persist entries with durable CDN URLs (skip base64 to save space)
          const durable = Array.from(
            (value.state.urls as Map<string, FileUrlEntry>).entries()
          )
            .filter(([, v]) => v.url)
            .map(([k, v]) => [k, { url: v.url, mediaType: v.mediaType }]);

          localStorage.setItem(
            name,
            JSON.stringify({
              ...value,
              state: { urls: durable },
            })
          );
        },
        removeItem: (name) => localStorage.removeItem(name),
      },
    }
  )
);
